import React from 'react';
import { Phone, MessageCircle, Star, CheckCircle, ArrowDown } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';

const HeroSection = () => {
  const scrollToServices = (e) => {
    e.preventDefault();
    const element = document.querySelector('#services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      style={{ background: 'var(--gradient-hero-warm)' }}
    >
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div 
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-40 blur-3xl animate-float"
          style={{ 
            background: 'radial-gradient(circle, var(--accent-blue-light) 0%, transparent 70%)',
            animationDelay: '0s'
          }}
        />
        <div 
          className="absolute top-1/2 -left-32 w-80 h-80 rounded-full opacity-30 blur-3xl animate-float"
          style={{ 
            background: 'radial-gradient(circle, var(--accent-green-light) 0%, transparent 70%)',
            animationDelay: '2s'
          }}
        />
        <div 
          className="absolute -bottom-20 right-1/4 w-72 h-72 rounded-full opacity-25 blur-3xl animate-float"
          style={{ 
            background: 'radial-gradient(circle, var(--accent-gold-light) 0%, transparent 70%)',
            animationDelay: '4s'
          }}
        />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(var(--navy-primary) 1px, transparent 1px),
              linear-gradient(90deg, var(--navy-primary) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center py-16 md:py-20 lg:py-24">
          
          {/* Trust Badge */}
          <ScrollReveal animation="fadeUp" delay={0}>
            <div 
              className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full mb-8 glass"
              style={{ border: '1px solid rgba(26, 62, 92, 0.1)' }}
            >
              <div className="flex -space-x-1">
                {[1,2,3,4,5].map((star) => (
                  <Star 
                    key={star} 
                    size={16} 
                    fill="var(--accent-gold)" 
                    color="var(--accent-gold)" 
                  />
                ))}
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                5-Star Rated Service in San Diego
              </span>
            </div>
          </ScrollReveal>

          {/* Main Headline */}
          <ScrollReveal animation="fadeUp" delay={100}>
            <h1 className="heading-hero mb-6 leading-tight">
              Professional Cleaning &
              <br />
              <span className="gradient-text">Home Organization</span>
            </h1>
          </ScrollReveal>

          {/* Subtitle */}
          <ScrollReveal animation="fadeUp" delay={200}>
            <p className="text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Transform your space with our premium cleaning services. 
              We bring meticulous attention to detail that makes every 
              corner of your home shine.
            </p>
          </ScrollReveal>

          {/* Feature Pills */}
          <ScrollReveal animation="fadeUp" delay={300}>
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {[
                { text: 'Reliable Service', icon: CheckCircle },
                { text: 'Flexible Hours', icon: CheckCircle },
                { text: 'Detail-Oriented', icon: CheckCircle }
              ].map((feature) => (
                <div 
                  key={feature.text}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium hover-scale"
                  style={{ 
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                    boxShadow: '0 2px 8px rgba(26, 62, 92, 0.06)'
                  }}
                >
                  <feature.icon size={16} style={{ color: 'var(--accent-green)' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{feature.text}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* CTA Buttons */}
          <ScrollReveal animation="fadeUp" delay={400}>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
              <a 
                href="#contact"
                onClick={scrollToContact}
                className="btn-primary py-4 px-10 text-base shadow-lg hover:shadow-xl"
                style={{ boxShadow: '0 8px 24px rgba(26, 62, 92, 0.25)' }}
              >
                Get Free Quote
              </a>
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="btn-secondary py-4 px-10 text-base"
              >
                <Phone size={18} />
                Call Now
              </a>
            </div>
          </ScrollReveal>

          {/* Quick Contact Info */}
          <ScrollReveal animation="fadeUp" delay={500}>
            <div 
              className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              <a 
                href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 hover:text-[var(--accent-blue)] transition-colors font-medium"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--accent-blue-light)' }}
                >
                  <Phone size={14} style={{ color: 'var(--accent-blue)' }} />
                </div>
                {contactInfo.phone}
              </a>
              <span className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" />
              <a 
                href={`https://wa.me/1${contactInfo.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hello! I'd like to request a free quote for cleaning services.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[var(--accent-green)] transition-colors font-medium"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: '#d4f5e2' }}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                WhatsApp
              </a>
            </div>
          </ScrollReveal>
        </div>

        {/* Scroll Indicator */}
        <ScrollReveal animation="fade" delay={800}>
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <button 
              onClick={scrollToServices}
              className="flex flex-col items-center gap-2 text-sm font-medium opacity-60 hover:opacity-100 transition-opacity"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>Explore Services</span>
              <ArrowDown size={20} className="animate-bounce" />
            </button>
          </div>
        </ScrollReveal>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg 
          viewBox="0 0 1440 100" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="none"
        >
          <path 
            d="M0 100L48 91.7C96 83.3 192 66.7 288 58.3C384 50 480 50 576 54.2C672 58.3 768 66.7 864 66.7C960 66.7 1056 58.3 1152 54.2C1248 50 1344 50 1392 50L1440 50V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0Z" 
            fill="var(--bg-page)"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
